/* eslint react/prop-types: 0 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect, useLocation } from 'react-router-dom';

import { AuthContext } from '../contexts/Auth';

import {
  decodeRedirect,
  encodeRedirect,
  getQuery,
  parseRedirect,
} from '../utils/url';
import { featuresPathRegex } from '../localization/features/countryFeatures';
import { canManageProjectAccess, PROJECT_ACCESS_PATH } from '../pages/Settings/ProjectAccess/access'; // TEMP Vietnam project access

const RouteAuthenticated = ({
  path,
  component: C,
  props: cProps,
  ...rest
}) => {
  const authContext = useContext(AuthContext);
  const {
    apps,
    user,
    hasHigherAccess,
    country,
  } = authContext.state;
  const location = useLocation();
  const currentPath = location.pathname;
  const hasAccess = () => {
    // TEMP Vietnam project access: HQ only. Checked here because the Vietnam settings path block
    // below would otherwise deny it.
    if (currentPath.startsWith(PROJECT_ACCESS_PATH) && (path === '/settings' || path === PROJECT_ACCESS_PATH)) {
      return canManageProjectAccess(user.role, country.features);
    }

    switch (path) {
      // LMS-2843
      // case '/settings/chats':
      case '/settings':
      case '/settings/fields':
      case '/settings/auto_assignment':
      case '/settings/workflow':
      case '/settings/call':
      case '/settings/api_integrations':
      case '/settings/buyer_access':
      case '/settings/credit_check':
      case '/customers/credit_check':
        if (user.role && user.role !== 'Headquarters') {
          return false;
        }
        if (path === '/settings/call' && !(apps.lead_management_v2 && apps.lead_management_v2.hasCallAccess)) {
          return false;
        }
        break;
      case '/campaigns/submissions':
      case '/audiences':
        if (!hasHigherAccess) {
          return false;
        }
        break;
      default:
    }

    // disable access if features is not available in country
    if (!country.features.promotionBlast) {
      if (featuresPathRegex.blast.some((regex) => regex.test(currentPath))) {
        return false;
      }
    }

    if (country.features.navLeads === false) {
      if (featuresPathRegex.leads.some((regex) => regex.test(currentPath))) {
        return false;
      }
    }

    if (country.features.navCampaigns === false) {
      if (featuresPathRegex.campaigns.some((regex) => regex.test(currentPath))) {
        return false;
      }
    }

    if (country.features.navSettings === false) {
      if (featuresPathRegex.settings.some((regex) => regex.test(currentPath))) {
        return false;
      }
    }

    return true;
  };

  const hasProductAccess = () => {
    if (!apps.lead_management_v2
        || !apps.lead_management_v2.hasAccess
        || !apps.showroom
        || !apps.showroom.hasAccess) {
      return false;
    }
    return true;
  };

  return (
    <Route
      {...rest}
      render={(props) => (
        <AuthContext.Consumer>
          {({ state }) => {
            const { redirect } = getQuery();

            if (state.isAuthenticated) {
              if (!hasAccess() || !hasProductAccess()) {
                return <Redirect to="/access_denied" />;
              }
              if (redirect) {
                let url = decodeRedirect(redirect);
                if (url.startsWith(window.location.origin)) {
                  url = url.replace(window.location.origin, '');
                }
                return <Redirect from="/" to={url} />;
              }
              return <C {...props} {...cProps} />;
            }
            // redirect to login screen;
            return <Redirect to={`/login${parseRedirect(redirect || encodeRedirect(window.location.href))}`} />;
          }}
        </AuthContext.Consumer>
      )}
    />
  );
};

RouteAuthenticated.propTypes = {
  component: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.node,
    PropTypes.object,
  ]).isRequired,
};

export default RouteAuthenticated;
